import { useState, useEffect, useMemo } from "react";
import {
  Outlet,
  Link,
  useLoaderData,
  useParams,
  NavLink,
  useNavigate,
} from "react-router-dom";

import { calculateScl90Results } from "./Scl90Calc";

import Scl90CardResults from "./Scl90CardResults";

import { scl90CardData } from "./Scl90CardData";

import * as API from "../services/scl90";
import * as APIPACIENTE from "../services/pacientes";
import * as APIUSER from "../services/user";

export async function loader({ params }) {
  const token = sessionStorage.getItem("token");

  // Si no hay token, simplemente NO llamamos a la API y retornamos null
  if (!token) {
    return null;
  }
  try {
    const scl90List = await API.getAllScl90ByTax(params.tratamientoId);

    // Verificar si `params.pacienteId` está disponible
    const pacienteMeta = params.pacienteId
      ? await APIPACIENTE.getPacienteByID(params.pacienteId)
      : await APIUSER.getCurrentUser();

    return { scl90List, pacienteMeta };
  } catch (error) {
    // Manejo del error, por ejemplo:
    throw new Response("Error al cargar los datos", { status: 500 });
  }
}

export function Scl90ComparativaItem(props) {
  const { scl90List, pacienteMeta } = useLoaderData();
  const sexPaciente = pacienteMeta.meta?.sexo_paciente || "hombre";
  const navigate = useNavigate();

  const renderEmptyMessage = () => {
    if (scl90List.length === 0) {
      return (
        <h1>
          Aquí podrás ver la evolución de tus resultados. Actualmente no existen
          datos para comparar.
        </h1>
      );
    }
    return null;
  };

  useEffect(() => {
    if (scl90List.length === 1 && props.userRole.includes("paciente")) {
      const objectId = scl90List[0].id; // Obtén el ID del único objeto
      navigate(`${objectId}`); // Navega a la URL con el ID
    }
  }, [scl90List, navigate, props.userRole]);

  const sortedScl90List = useMemo(() => {
    return [...scl90List].sort((a, b) => {
      const dateA = new Date(a.meta.metaFecha);
      const dateB = new Date(b.meta.metaFecha);
      return dateA - dateB;
    });
  }, [scl90List]);

  // Función de conteo independiente
  const countResponses = (responses) => {
    // Asumiendo que las claves de las respuestas siguen el patrón 'scl90PregXXX'
    return Object.keys(responses).filter(
      (key) => key.startsWith("scl90Preg") && responses[key] !== ""
    ).length;
  };

  // Definir la función de formateo de fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Transformar la lista
  const transformedScl90List = useMemo(() => {
    return sortedScl90List
      .map((scl90) => {
        const responses = scl90.meta; // Extraer las respuestas originales
        const responseCount = countResponses(responses); // Contar respuestas no vacías
  
        if (responseCount <= 80) {
          // Menos del 90% de respuestas
          return null; // Excluir este cuestionario
        }
  
        const results = calculateScl90Results(responses); // Calcula los resultados
  
        return {
          fecha: formatDate(scl90.meta.metaFecha),
          ...results, // Agregar los resultados calculados al objeto transformado
          responseCount, // Añadir el conteo de respuestas (opcional)
        };
      })
      .filter((item) => item !== null); // Eliminar los cuestionarios incompletos
  }, [sortedScl90List]);

  // Filtro de percentiles por sexo
  const percSex = useMemo(() => ({
    percSom: sexPaciente === "hombre" ? "percSomHomb" : "percSomMuj",
    percObs: sexPaciente === "hombre" ? "percObsHomb" : "percObsMuj",
    percSi: sexPaciente === "hombre" ? "percSiHomb" : "percSiMuj",
    percDep: sexPaciente === "hombre" ? "percDepHomb" : "percDepMuj",
    percAns: sexPaciente === "hombre" ? "percAnsHomb" : "percAnsMuj",
    percHos: sexPaciente === "hombre" ? "percHosHomb" : "percHosMuj",
    percFob: sexPaciente === "hombre" ? "percFobHomb" : "percFobMuj",
    percPar: sexPaciente === "hombre" ? "percParHomb" : "percParMuj",
    percPsic: sexPaciente === "hombre" ? "percPsicHomb" : "percPsicMuj",
    percGsi: sexPaciente === "hombre" ? "percGsiHomb" : "percGsiMuj",
    percPst: sexPaciente === "hombre" ? "percPstHomb" : "percPstMuj",
    percPsdi: sexPaciente === "hombre" ? "percPsdiHomb" : "percPsdiMuj",
    // El resultado de ADI no tiene percentil por sexo, pero se añade para que siga apareciendo en el .map
    totalDimenAdi: "totalDimenAdi",
  }), [sexPaciente]);

  return (
    <>
      <div id="content" className="comparativa-graficas-container">
        {renderEmptyMessage() || (
          <form className="analitic-form">
            <h3>{ props.userRole.includes("paciente") ? "Síntomas" : "Cuestionario SCL90" }</h3>
            <div className="five-col-analitic">
            {scl90CardData.map((chart) => {
                const key = percSex[chart.dataKey];
                if (!key || !transformedScl90List.some((item) => key in item)) {
                  console.warn(
                    `Data key "${key}" is missing in transformedScl90List`
                  );
                  return null;
                }
                return (
                  <Scl90CardResults
                    key={chart.dataKey}
                    title={chart.title}
                    tooltipText={chart.tooltipText}
                    dataKey={key}
                    transformedScl90List={transformedScl90List}
                    percSex={percSex}
                  />
                );
              })}
            </div>
          </form>
        )}
      </div>
      <div id="actions">
        {!props.userRole.includes("paciente") && (
          <Link className="button is-link" to={`new`}>
            Nuevo
          </Link>
        )}
      </div>
    </>
  );
}
